import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getColor } from "../../utils/colorfunctions";
import { mixDisplayName } from "../../utils/mixDisplayName";
import PdfModal from "./PdfModal";
import "./ProductInfoPage.css";
import "./PublicCompliance.css";

const publicClient = axios.create({ baseURL: "" });

function ComponentCard({ comp, companySlug }) {
    const navigate = useNavigate();
    const colorKey = (comp.color || "").toLowerCase();
    const accentColor = getColor(colorKey); 
    const needsLightText = colorKey === "navy" || colorKey === "purple" || colorKey === "synthetic";

    const safetyFields = ["microbials", "pesticides", "heavy_metals", "foreign_matter", "mycotoxins"];
    const allSafetyPass = safetyFields.every(
        (f) => typeof comp[f] === "string" && comp[f].toLowerCase().includes("pass")
    );
    const anySafetyFail = safetyFields.some(
        (f) => typeof comp[f] === "string" && comp[f].toLowerCase().includes("fail")
    );

    let contaminantStatus = "neutral";
    let contaminantLabel = "Not Tested";
    if (allSafetyPass) {
        contaminantStatus = "pass";
        contaminantLabel = "Pass";
    } else if (anySafetyFail) {
        contaminantStatus = "fail";
        contaminantLabel = "Fail";
    }

    const d9 = comp.delta_9_THC != null ? parseFloat(comp.delta_9_THC) : null;
    const hasCannabinoids = comp.THCa != null || comp.CBD != null || comp.CBG != null;
    let thcStatus = "neutral";
    let thcLabel = "Not Tested";
    if (d9 !== null && !isNaN(d9) && d9 > 0) {
        if (d9 <= 0.3) {
            thcStatus = "pass";
            thcLabel = "Delta-9 Below 0.3%";
        } else {
            thcStatus = "fail";
            thcLabel = "Delta-9 Above 0.3%";
        }
    } else if (hasCannabinoids) {
        thcStatus = "pass";
        thcLabel = "Passed THC Test";
    }

    return (
        <div className="pc-coa-card">
            <div className="pc-coa-card__header">
                <div className="pc-coa-card__title-row">
                    <img className="pc-coa-card__badge-icon" src="/DYC-icon-ColorWheel.png" alt="DYC" />
                    <div
                        className="pc-coa-card__name-banner"
                        style={accentColor ? { backgroundColor: accentColor, color: needsLightText ? "#ffffff" : undefined } : undefined}
                    >
                        <h3 className="pc-coa-card__name">{comp.product_name}</h3>
                    </div>
                </div>
                <div className="pc-coa-card__meta-row">
                    {comp.dycId && (
                        <span className="pc-coa-card__dyc-id">{comp.dycId}</span>
                    )}
                    {comp.ratio_pct != null && (
                        <span className="pc-coa-card__dyc-id">{parseFloat(comp.ratio_pct).toFixed(1)}% of mix</span>
                    )}
                </div>
            </div>

            <div className="pc-compliance-rows">
                <div className={`pc-compliance-row pc-compliance-row--${contaminantStatus}`}>
                    <span className={`pc-compliance-row__icon pc-compliance-row__icon--${contaminantStatus}`}>
                        {contaminantStatus === "pass" ? "✓" : contaminantStatus === "fail" ? "✗" : "—"}
                    </span>
                    <span className="pc-compliance-row__label">Contaminants</span>
                    <span className={`pc-compliance-row__status pc-compliance-row__status--${contaminantStatus}`}>
                        {contaminantLabel}
                    </span>
                </div>
                <div className={`pc-compliance-row pc-compliance-row--${thcStatus}`}>
                    <span className={`pc-compliance-row__icon pc-compliance-row__icon--${thcStatus}`}>
                        {thcStatus === "pass" ? "✓" : thcStatus === "fail" ? "✗" : "—"}
                    </span>
                    <span className="pc-compliance-row__label">THC Content</span>
                    <span className={`pc-compliance-row__status pc-compliance-row__status--${thcStatus}`}>
                        {thcLabel}
                    </span>
                </div>
            </div>

            <div className="pc-card-actions">
                {comp.coa_id && (
                    <button className="pc-card-actions__btn pc-card-actions__btn--full" onClick={() => navigate(`/compliance/${companySlug}/product/${comp.coa_id}`, {
                        state: { coa: comp },
                    })}>
                        View Product Info
                    </button>
                )}
            </div>
        </div>
    );
}

export default function MixInfoPage() {
    const { companySlug, productId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pdfOpen, setPdfOpen] = useState(false);

    const { pdfUrls, pdfLabels } = useMemo(() => {
        const urls = [];
        const labels = [];
        for (const comp of data?.components || []) {
            for (const url of comp.pdfUrls || []) {
                urls.push(url);
                labels.push(comp.product_name || "Unknown");
            }
        }
        return { pdfUrls: urls, pdfLabels: labels };
    }, [data]);

    useEffect(() => {
        document.title = "Mix Product Info — DYC";
        return () => { document.title = "DYC Terminal"; };
    }, []);

    useEffect(() => {
        if (!companySlug || !productId) return;
        setLoading(true);
        publicClient
            .get(`/public/compliance/${companySlug}/mix/${productId}`)
            .then((res) => {
                setData(res.data);
                document.title = `${res.data.product?.product_name || "Mix"} — Product Info`;
            })
            .catch((err) => {
                setError(
                    err.response?.status === 404
                        ? "Mix product not found."
                        : "Something went wrong. Please try again later."
                );
            })
            .finally(() => setLoading(false));
    }, [companySlug, productId]);

    if (loading) {
        return (
            <div className="pi-page">
                <button className="pi-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>
                <div className="pi-loading">
                    <div className="pi-spinner" />
                    <span>Loading mix info&hellip;</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pi-page">
                <button className="pi-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>
                <div className="pi-error">{error}</div>
            </div>
        );
    }

    if (!data) return null;

    const { product, components } = data;
    const typeLabel = product.product_type === "pre-roll" ? "Pre-Roll" : "Mix";

    return (
        <div className="pi-page">
            <div className="pi-topbar">
                <button className="pi-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>
                {pdfUrls.length > 0 && (
                    <button className="pi-view-files-btn" onClick={() => setPdfOpen(true)}>
                        View COA Files
                    </button>
                )}
            </div>

            <div className="pi-header">
                <div className="pi-header__top">
                    <img className="pi-header__icon" src="/DYC-icon-ColorWheel.png" alt="DYC" />
                    <div className="pi-header__title-group">
                        <h1 className="pi-header__name">{mixDisplayName(product.product_name, product.product_type, product.created_at)}</h1>
                        <div className="pi-header__tags">
                            <span className="pi-header__type-badge">{typeLabel}</span>
                            <span className="pi-header__type-badge pi-header__type-badge--mix">
                                {components.length} Components
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pi-section">
                <div className="pi-section__header">
                    <h2 className="pi-section__title">Constituent Components</h2>
                    <p className="pi-section__subtitle">
                        Each component's lab results shown individually — COA data is not combined or averaged
                    </p>
                </div>

                <div className="pc-coa-grid">
                    {components.map((comp, i) => (
                        <ComponentCard key={comp.coa_id || i} comp={comp} companySlug={companySlug} />
                    ))}
                </div>
            </div>

            <footer className="pi-footer">
                <span>Powered by DYC &mdash; Designate Your Chemotype</span>
            </footer>

            {pdfOpen && <PdfModal urls={pdfUrls} labels={pdfLabels} onClose={() => setPdfOpen(false)} />}
        </div>
    );
}
